import React, { useState } from 'react';
import { Button, Modal, Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import { RoundedButton } from '../../styles/CustomButtons';
import TemplatePicker from '../Pickers/TemplatePicker';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import APIService from '../../services/APIService';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useEventTracking } from '@services/statsig/hooks';
import { getNoteRoute } from '@utils/noteRouting';

interface SwitchTemplateButtonProps {
  noteId?: string;
  currentTemplateName?: string;
  currentTemplateId?: string;
  encounterId?: string;
  demoMode?: boolean;
  showTooltip?: boolean;
  onSuccess?: () => void;
}

const SwitchTemplateButton: React.FC<SwitchTemplateButtonProps> = ({
  noteId,
  currentTemplateName,
  currentTemplateId,
  encounterId,
  demoMode = false,
  showTooltip = false,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { template: templateMetrics, note: noteMetrics } = useEventTracking();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateIdToRegenerate, setTemplateIdToRegenerate] = useState<string>();
  const [isRegeneratingNote, setIsRegeneratingNote] = useState(false);

  const handleOpenModal = () => {
    // Track modal opened
    if (noteId) {
      noteMetrics.templateSwitchModalOpened(noteId, {
        current_template_id: currentTemplateId,
        current_template_name: currentTemplateName,
        encounter_id: encounterId,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleTemplateSelect = (template: { template_id: string; display_name?: string } | undefined) => {
    if (template?.template_id) {
      // Track template selection
      noteMetrics.templateSelected({
        note_id: noteId,
        current_template_id: currentTemplateId,
        new_template_id: template.template_id,
        new_template_name: template.display_name,
        encounter_id: encounterId,
      });
      setTemplateIdToRegenerate(template.template_id);
    }
  };

  const handleRegenerateNoteWithNewTemplate = async () => {
    if (!templateIdToRegenerate || !noteId) {
      return;
    }

    if (demoMode) {
      return;
    }

    // Track regenerate button clicked
    noteMetrics.templateRegenerateClicked(noteId, {
      current_template_id: currentTemplateId,
      new_template_id: templateIdToRegenerate,
      encounter_id: encounterId,
    });

    setIsRegeneratingNote(true);

    try {
      // Get access token from localStorage for simplicity
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        setIsRegeneratingNote(false);
        enqueueSnackbar('Authentication failed. Please try again.', {
          variant: 'error',
        });
        return;
      }

      const response = await APIService.makeAPIPostRequest({
        requestString: '/noteProcessingSession/retryNoteProcessingWithNewTemplate',
        accessToken: accessToken,
        body: {
          note_id: noteId,
          template_id: templateIdToRegenerate,
        },
      });

      if (response.ok) {
        handleCloseModal();
        if (onSuccess) {
          onSuccess();
        }
        enqueueSnackbar('Note regeneration started successfully!', {
          variant: 'success',
        });
        navigate(getNoteRoute({ note_id: response.value.note_id, thread_id: response.value.thread_id }));
      } else {
        enqueueSnackbar('Failed to regenerate note. Please try again.', {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar('An error occurred while regenerating the note.', {
        variant: 'error',
      });
    } finally {
      setIsRegeneratingNote(false);
    }
  };

  const tooltipText = currentTemplateName
    ? `Switch from "${currentTemplateName}" to a different template`
    : 'Switch to a different note template';

  const button = (
    <RoundedButton
      variant="outlined"
      sx={{
        fontSize: '0.85rem',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        px: 2,
        ml: 1,
        bgcolor: 'backgroundColors.secondary',
        borderColor: 'borderColors.lightprimary',
        border: '0.1px solid',
      }}
      onClick={() => {
        // Track switch template button clicked
        if (noteId) {
          noteMetrics.templateSwitchClicked(noteId, {
            current_template_id: currentTemplateId,
            current_template_name: currentTemplateName,
            encounter_id: encounterId,
          });
        }
        handleOpenModal();
      }}
    >
      <SwapHorizIcon sx={{ fontSize: '1rem', mr: 1 }} />
      Switch Template
    </RoundedButton>
  );

  return (
    <>
      {showTooltip ? (
        <Tooltip title={tooltipText} placement="top" arrow>
          {button}
        </Tooltip>
      ) : (
        button
      )}

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="switch-template-modal"
        aria-describedby="modal-to-switch-note-template"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '60%' },
            maxWidth: '600px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Switch Note Template
          </Typography>

          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Current template: <strong>{currentTemplateName || 'Not specified'}</strong>
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, color: 'textColors.primary' }}>
            Would you like to regenerate this note with a different template?
          </Typography>

          <TemplatePicker onTemplateSelect={handleTemplateSelect} />

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <RoundedButton variant="outlined" onClick={handleCloseModal}>
              Cancel
            </RoundedButton>

            <RoundedButton
              variant="contained"
              color="primary"
              onClick={handleRegenerateNoteWithNewTemplate}
              disabled={!templateIdToRegenerate || isRegeneratingNote}
            >
              {isRegeneratingNote ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  Regenerating...
                </>
              ) : (
                'Regenerate Note'
              )}
            </RoundedButton>
          </Box>

          <Typography variant="caption" color="error" sx={{ mt: 2, alignSelf: 'flex-end' }}>
            The new note will be generated with the selected template. This note will remain in your
            account unless you delete it.
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

export default SwitchTemplateButton;
